import { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import type { RefObject, ReactNode } from 'react';

interface PortalDropdownProps {
  /** Ref to the trigger element (button) that opens the dropdown */
  anchorRef: RefObject<HTMLElement | null>;
  /** Whether the dropdown is open */
  isOpen: boolean;
  /** Called when the dropdown should close (outside click) */
  onClose: () => void;
  /** Dropdown content */
  children: ReactNode;
  /** Width of the dropdown in px */
  width?: number;
  /** Horizontal alignment relative to anchor */
  align?: 'left' | 'right';
}

export function PortalDropdown({
  anchorRef,
  isOpen,
  onClose,
  children,
  width = 256,
  align = 'left',
}: PortalDropdownProps) {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const scrollY = window.scrollY;
    const scrollX = window.scrollX;

    let left: number;
    if (align === 'right') {
      left = rect.right + scrollX - width;
    } else {
      left = rect.left + scrollX;
    }

    // Clamp to viewport
    const viewportWidth = document.documentElement.clientWidth;
    if (left + width > viewportWidth + scrollX) {
      left = viewportWidth + scrollX - width - 8;
    }
    if (left < scrollX) {
      left = scrollX + 8;
    }

    setPosition({
      top: rect.bottom + scrollY + 4,
      left,
    });
  }, [anchorRef, width, align]);

  // Update position when opened and on scroll/resize
  useEffect(() => {
    if (!isOpen) return;
    updatePosition();

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, updatePosition]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        anchorRef.current &&
        !anchorRef.current.contains(target)
      ) {
        onClose();
      }
    };
    // Use timeout to avoid closing immediately from the same click that opened it
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClick);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [isOpen, onClose, anchorRef]);

  if (!isOpen) return null;

  return createPortal(
    <div
      ref={dropdownRef}
      className="bg-dark-surface border border-dark-border rounded-lg shadow-lg"
      style={{
        position: 'absolute',
        top: position.top,
        left: position.left,
        width,
        zIndex: 9999,
      }}
    >
      {children}
    </div>,
    document.body
  );
}
